import { ClockIcon, SettingsIcon, SpinnerIcon } from '@/components/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { App, Button, Dropdown, Switch } from 'antd';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  clearChannelOverride,
  getOpenNow,
  listChannels,
  listHours,
  setChannelEnabled,
  setChannelOverride,
} from '@/api/pharmacies.api';
import { useActivePharmacy } from '@/auth/active-pharmacy';
import { useAuth } from '@/auth/auth-context';
import { errorMessage } from '@/lib/errors';
import { scheduleEdge, whenLabel } from '@/lib/opening-hours';
import { queryKeys } from '@/lib/query-keys';
import { inZone, nowInZone } from '@/lib/time';

/**
 * How long a hold lasts, in either direction.
 *
 * Every one is bounded, because an override nobody clears is how a shop drifts
 * dark for a week after one bad afternoon — or, now that it can be held open,
 * how it drifts into taking orders at 3am. The server caps it at 12 hours for
 * the same reason.
 */
const DURATIONS = [
  { minutes: 30, label: '30 min' },
  { minutes: 60, label: '1 hour' },
  { minutes: 120, label: '2 hours' },
  { minutes: 240, label: '4 hours' },
] as const;

const spell = (minutes: number) => (minutes < 60 ? `${minutes} minutes` : `${minutes / 60}h`);

/**
 * Online availability, always on screen and changeable in one tap.
 *
 * Going offline is urgent and unplanned — a power cut, a queue out of the door,
 * the pharmacist stepping out. Burying it on a settings page means it gets used
 * late or not at all, and a shop that is shut but still listed is exactly how a
 * rider ends up at a shuttered door. So the switch lives here, not there.
 *
 * **Online only.** The counter has no switch anywhere in this app: staff can
 * always serve someone standing in front of them, and a control that cannot
 * stop them doing it is a control that lies. The COUNTER channel still exists
 * on the server — it is what tells the platform whether walk-ins are being
 * served — but nothing in the till consults it.
 *
 * The pill shows the REAL answer rather than the switch position: the schedule,
 * any override and any planned closure all count, so "Taking orders" means
 * customers can genuinely order right now.
 *
 * ## The panel offers ONE action, and it is the one that fits
 *
 * This panel used to offer "Pause for 30 min / 1 hour / …" whenever the online
 * switch was on — including while the shop was **already shut** for the night.
 * So the one moment a pharmacist opens it wanting to trade, it offered four
 * buttons to stop trading and nothing to start. It could even render "Held open
 * past your usual hours", a state nothing in the app could produce.
 *
 * Now the action follows the state: open late when you are shut, pause when you
 * are trading, and go back to your hours when a hold is on.
 *
 * ## What an override can and cannot beat
 *
 * Taken from the resolver in `pharmacies.service.ts :: openNow`, which is the
 * authority:
 *
 * ```
 * isOpen = channelEnabled AND (override ?? withinSchedule) AND NOT plannedClosure
 * ```
 *
 * Two things follow, and both are why this file branches the way it does:
 *
 *   **A planned closure cannot be overridden.** It is an `AND NOT` outside the
 *   override entirely, so "Open now" would report success and change nothing.
 *   The panel does not offer it; it says what is holding the shop and points at
 *   the page that can lift it.
 *
 *   **The master switch sits ABOVE the override, not below it.** The backend
 *   README reads "planned closure > live override > schedule > channel switch",
 *   which suggests an override outranks the switch. The code says otherwise —
 *   `channelEnabled` is a plain AND. So holding the shop open while the switch
 *   is off does nothing at all, and `goLive` turns the switch on first rather
 *   than writing an override that could not win.
 */
export function AvailabilityPill() {
  const { message } = App.useApp();
  const { can } = useAuth();
  const { pharmacyId } = useActivePharmacy();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);

  const canEdit = can('pharmacy:operations:write');

  const openNow = useQuery({
    queryKey: queryKeys.openNow(pharmacyId ?? ''),
    queryFn: () => getOpenNow(pharmacyId!),
    enabled: Boolean(pharmacyId),
    // Visibility turns over on the clock, with nobody touching the app.
    refetchInterval: 60_000,
  });

  const channels = useQuery({
    queryKey: queryKeys.channels(pharmacyId ?? ''),
    queryFn: () => listChannels(pharmacyId!),
    enabled: Boolean(pharmacyId),
  });

  /*
   * The weekly schedule, only so the panel can say WHEN this changes.
   *
   * Fetched lazily — a shop's hours are not needed to draw the pill itself, and
   * this is mounted on every screen in the app.
   */
  const hours = useQuery({
    queryKey: queryKeys.hours(pharmacyId ?? ''),
    queryFn: () => listHours(pharmacyId!),
    enabled: Boolean(pharmacyId) && open,
    staleTime: 10 * 60_000,
  });

  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.channels(pharmacyId ?? '') }),
      queryClient.invalidateQueries({ queryKey: queryKeys.openNow(pharmacyId ?? '') }),
    ]);

  const toggle = useMutation({
    mutationFn: (isEnabled: boolean) => setChannelEnabled(pharmacyId!, 'ONLINE', isEnabled),
    onSuccess: async (_, isEnabled) => {
      await refresh();
      message.success(isEnabled ? 'Online orders are on' : 'Online orders are off');
    },
    onError: (e: Error) => message.error(errorMessage(e)),
  });

  const pause = useMutation({
    mutationFn: (minutes: number) =>
      setChannelOverride(pharmacyId!, 'ONLINE', { state: 'CLOSED', minutes }),
    onSuccess: async (_, minutes) => {
      await refresh();
      setOpen(false);
      message.success(`Paused for ${spell(minutes)}`);
    },
    onError: (e: Error) => message.error(errorMessage(e)),
  });

  /**
   * Open the shop right now, whatever is currently holding it shut.
   *
   * Two writes when the master switch is off, and **the order is load-bearing
   * in both directions**:
   *
   *   An override cannot win against a disabled channel, so the switch has to
   *   go on first or the hold does nothing.
   *
   *   `setChannelEnabled(false)` deliberately CLEARS any live override (see the
   *   comment on it in `pharmacies.service.ts` — a stale hold would silently
   *   reapply on the next switch-on). Writing the override first and the switch
   *   second would therefore wipe the override that had just been written, and
   *   report success having changed nothing.
   *
   * Sequential rather than parallel so a failure has one meaning, and the
   * failure message names what DID land — a pharmacist told only "failed",
   * while the switch quietly went on, has been lied to about their own shop.
   * Enabling ONLINE also requires a verified licence, so this legitimately
   * fails with the server's own sentence on a shop whose licence has lapsed.
   */
  const goLive = useMutation({
    mutationFn: async (minutes: number) => {
      const needsSwitch = !channels.data?.find((c) => c.channel === 'ONLINE')?.isEnabled;
      if (needsSwitch) {
        await setChannelEnabled(pharmacyId!, 'ONLINE', true);
        try {
          await setChannelOverride(pharmacyId!, 'ONLINE', { state: 'OPEN', minutes });
        } catch (error) {
          throw new Error(
            `Online orders are back on, but the shop could not be held open past your hours: ${errorMessage(error)}`,
          );
        }
        return;
      }
      await setChannelOverride(pharmacyId!, 'ONLINE', { state: 'OPEN', minutes });
    },
    onSuccess: async (_, minutes) => {
      await refresh();
      setOpen(false);
      message.success(`Taking orders for the next ${spell(minutes)}`);
    },
    onError: (e: Error) => message.error(errorMessage(e)),
  });

  const resume = useMutation({
    mutationFn: () => clearChannelOverride(pharmacyId!, 'ONLINE'),
    onSuccess: async () => {
      await refresh();
      message.success('Back on your usual hours');
    },
    onError: (e: Error) => message.error(errorMessage(e)),
  });

  if (!pharmacyId) return null;

  const live = openNow.data?.isOpen ?? false;
  const online = channels.data?.find((c) => c.channel === 'ONLINE');
  const override = openNow.data?.channels?.ONLINE.overrideState ?? null;
  const overrideUntil = openNow.data?.channels?.ONLINE.overrideUntil ?? null;
  const onClosure = openNow.data?.onAdHocClosure ?? false;
  const switchedOff = Boolean(online) && !online!.isEnabled;
  const busy = toggle.isPending || pause.isPending || resume.isPending || goLive.isPending;

  /*
   * What is actually holding the shop in this state — ONE cause, in the order
   * the resolver applies them, so the sentence and the button below it are
   * talking about the same thing.
   */
  const cause = onClosure
    ? 'closure'
    : switchedOff
      ? 'switch'
      : override === 'CLOSED'
        ? 'paused'
        : override === 'OPEN'
          ? 'held-open'
          : live
            ? 'trading'
            : 'schedule';

  const now = nowInZone();
  const edge = scheduleEdge(hours.data, now);

  /** The second line: why, in the shop's own terms. */
  const because: Record<typeof cause, string> = {
    closure: 'A planned closure is on, and it beats the switch, your hours and any hold.',
    switch: 'Online orders are switched off.',
    paused: 'Paused by hand.',
    'held-open': 'Held open past your usual hours.',
    schedule: "Outside today's hours.",
    trading: 'On your usual hours.',
  };

  /**
   * The third line: when this changes by itself.
   *
   * The whole reason the schedule is fetched. "Outside today's hours" tells
   * somebody nothing they can act on; "opens at 9:00 am tomorrow" tells them
   * whether to wait or to open up.
   */
  const changesAt =
    override && overrideUntil
      ? `${override === 'OPEN' ? 'Open until' : 'Resumes'} ${inZone(overrideUntil).format('h:mm a')}`
      : cause === 'trading' && edge.state === 'open'
        ? `Closes ${whenLabel(edge.at, now)}`
        : cause === 'schedule' && edge.state === 'closed' && edge.at
          ? `Opens ${whenLabel(edge.at, now)}`
          : null;

  /**
   * The one action that fits the state.
   *
   * Never two. A panel that offers both "pause" and "open now" makes the reader
   * work out which of them applies, which is the job this is meant to do for
   * them.
   */
  const action = () => {
    if (!canEdit) return null;

    // Nothing here can lift a planned closure, so nothing here pretends to.
    if (cause === 'closure') {
      return (
        <div className="panel-section">
          <p className="panel-hint mt-0!">
            Remove the closure in Opening hours &amp; closures to start taking orders again.
          </p>
        </div>
      );
    }

    if (override) {
      return (
        <div className="panel-section">
          <div className="panel-choice panel-choice-1">
            <button
              type="button"
              className="panel-opt"
              disabled={busy}
              onClick={() => resume.mutate()}
            >
              {resume.isPending ? <SpinnerIcon /> : null}
              {override === 'OPEN' ? 'Stop early' : 'Resume now'}
            </button>
          </div>
          <p className="panel-hint">Your usual hours take over immediately.</p>
        </div>
      );
    }

    const opening = !live;
    const run = opening ? goLive : pause;

    return (
      <div className="panel-section">
        <span className="panel-label">{opening ? 'Take orders for' : 'Pause for'}</span>
        <div className="panel-choice panel-choice-4">
          {DURATIONS.map((option) => (
            <button
              key={option.minutes}
              type="button"
              className={`panel-opt ${opening ? 'panel-opt-go' : ''}`}
              disabled={busy}
              onClick={() => run.mutate(option.minutes)}
            >
              {run.isPending && run.variables === option.minutes ? (
                <SpinnerIcon />
              ) : (
                option.label
              )}
            </button>
          ))}
        </div>
        <p className="panel-hint">
          {opening
            ? switchedOff
              ? 'Turns online orders back on and holds the shop open. Your usual hours take over afterwards.'
              : 'Holds the shop open past your usual hours. It ends by itself.'
            : 'Your usual hours take over when it ends.'}
        </p>
      </div>
    );
  };

  /*
   * The same panel the account menu draws, down to the class names.
   *
   * It was a `Popover` full of Tailwind utilities and antd Buttons, three
   * inches from a dropdown built out of `.acct-*` — two panels in one column
   * with two vocabularies, which is what made the header read as a different
   * product from the sidebar. A `Dropdown` with `popupRender` rather than a
   * `Popover` for the same reason: Popover paints its own inner surface and an
   * arrow, so the panel's border and shadow would have landed inside a second
   * one.
   */
  const panel = (
    <div className="panel">
      <div className="panel-head">
        <span
          className={`panel-dot ${live ? 'panel-dot-live' : 'panel-dot-stopped'}`}
          aria-hidden
        />
        <div className="min-w-0">
          <div className="panel-title">
            {live ? 'Customers can order from you' : 'Customers cannot order from you'}
          </div>
          <span className="panel-note">{because[cause]}</span>
          {changesAt ? <span className="panel-note">{changesAt}</span> : null}
        </div>
      </div>

      {action()}

      {canEdit ? (
        /*
         * The standing setting, under a rule and below the right-now control.
         *
         * It was the first thing in the panel, which put a decision about
         * whether to be on the marketplace at all above the decision about the
         * next two hours — and the two got flipped for each other constantly.
         */
        <div className="panel-section">
          <div className="panel-row">
            <div className="min-w-0">
              <div className="panel-title">Online orders</div>
              <span className="panel-note">
                {online?.isEnabled ? 'Listed on the marketplace.' : 'Not listed at all.'}
              </span>
            </div>
            <Switch
              checked={Boolean(online?.isEnabled)}
              loading={toggle.isPending}
              disabled={busy && !toggle.isPending}
              onChange={(checked) => toggle.mutate(checked)}
              aria-label="Online orders"
            />
          </div>
        </div>
      ) : null}

      <div className="panel-actions">
        <button
          type="button"
          className="panel-action"
          onClick={() => {
            setOpen(false);
            navigate('/hours');
          }}
        >
          <span className="panel-action-icon">
            <SettingsIcon />
          </span>
          Opening hours &amp; closures
        </button>
      </div>
    </div>
  );

  return (
    <Dropdown
      open={open}
      onOpenChange={setOpen}
      trigger={['click']}
      placement="bottomRight"
      popupRender={() => panel}
    >
      <Button loading={busy} className="topbar-chip">
        <span className="flex items-center gap-2">
          <span
            className={`inline-block h-2.5 w-2.5 rounded-full ${live ? 'bg-positive' : 'bg-caution'}`}
            aria-hidden
          />
          {/*
            "Taking orders", never "Online"/"Offline".

            This pill is about the SHOP; the one beside it is about the network.
            Labelling both with the same two words produced a navbar that read
            "Online  Offline" side by side and meant nothing to anyone.
          */}
          <span className="hidden sm:inline">{live ? 'Taking orders' : 'Not taking orders'}</span>
          {/* A hold is on in either direction, so the clock is worth showing. */}
          {override ? <ClockIcon className="opacity-60" aria-label="On a hold" /> : null}
        </span>
      </Button>
    </Dropdown>
  );
}
